import CtaButton from '@/atoms/Buttons/CTA/CtaButton';
import useAbTest from '@/hooks/use-ab-test/use-ab-test';
import useBasket from '@/hooks/use-basket/use-basket';
import usePersistBasketStatus from '@/hooks/use-persist-basket-status/use-persist-basket-status';
import {increaseBasketCount} from '@/redux/actions/settingsActions';
import constantsFactory from '@/utils/constants';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {StyledChevronRightIcon} from './styles';
import Props from './typings';

const {
  TRANSLATION: {BUTTON},
} = constantsFactory();

const AB_QUERY_KEY = 'exp';
export const AB_VALUES = {
  CTA_BUY_NOW: '1172',
} as const;

export const getButtonLabel = ({
  basket,
  product,
  isBuyNow,
}: {
  basket: ReturnType<typeof useBasket>;
  product: Props['product'];
  isBuyNow: boolean;
}) => {
  let label: string = isBuyNow ? BUTTON.BUY_NOW : BUTTON.ADD_TO_BASKET;
  const isAlreadyIn = basket.contains(product);
  const isPreventCheckout = product.holiday_prevent_checkout;
  const isOutOfStock = !product.is_in_stock;

  if (isAlreadyIn) label = BUTTON.ALREADY_IN_YOUR_BASKET;
  if (isPreventCheckout) label = BUTTON.SHOP_CLOSED_FOR_ORDERS;
  if (isOutOfStock) label = BUTTON.SOLD;

  return label;
};

export const getConvertId = (title: string) => {
  let dataConvertId = title === BUTTON.BUY_NOW || title === 'BUY NOW' ? 'buy-now' : undefined;
  if (title === BUTTON.ADD_TO_BASKET || title === 'Add to Basket') {
    dataConvertId = 'add-to-basket';
  }

  return dataConvertId;
};

const BuyNowButton = ({product, currency, maxWidth}: Props) => {
  const {t, ready} = useTranslation();
  const basket: any = useBasket();
  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(
    basket.contains(product) || !product.is_in_stock || product.holiday_prevent_checkout,
  );
  const experiment = useAbTest<(typeof AB_VALUES)[keyof typeof AB_VALUES]>(AB_QUERY_KEY);
  const isBuyNow = experiment === AB_VALUES.CTA_BUY_NOW;

  /**
   *  This function sets the default title,
   * - when i18n is not ready yet
   * - needs to be here until the i18n is fixed on server.
   * @return {string}
   */
  const defaultTitle = (): string => {
    let btnText = isBuyNow ? 'BUY NOW' : 'Add to Basket';
    if (basket.contains(product)) btnText = 'Already in your basket';
    if (product.holiday_prevent_checkout) btnText = 'Shop closed for orders';
    if (!product.is_in_stock) btnText = 'Sold';
    return btnText;
  };
  const [title, setTitle] = useState(defaultTitle);
  const [, setBasketEmpty] = usePersistBasketStatus();
  const translatedTitle = t(title);
  const isChevronIcon = title === BUTTON.BUY_NOW || title === 'BUY NOW';
  const dataConvertId = getConvertId(title);

  const onClick = () => {
    setIsDisabled(true);
    setTitle(BUTTON.ADDING_TO_BASKET);
    dispatch(increaseBasketCount(1));
    setBasketEmpty(false);
    basket.addAndRedirect(product, currency);
  };

  useEffect(() => {
    if (!!basket && !!ready) {
      const newLabel = getButtonLabel({basket, product, isBuyNow});
      setTitle(newLabel);
      setIsDisabled(
        basket.contains(product) || !product.is_in_stock || product.holiday_prevent_checkout,
      );
    }
  }, [basket, ready, isBuyNow]);

  return (
    <CtaButton
      title={translatedTitle}
      disabled={isDisabled}
      onClick={onClick}
      maxWidth={maxWidth}
      dataConvertId={dataConvertId}
    >
      {translatedTitle}
      {isChevronIcon && <StyledChevronRightIcon type="chevron-right" fontSize={12} />}
    </CtaButton>
  );
};

export default BuyNowButton;
